import { useMemo, useContext } from "react";

import AppDialog from "components/AppDialog";
import SkillBadge from "components/SkillBadge";
import { sortSkillsByExperience } from "components/UserCardList/components/UserCardResult/constants";
import { JobsContext } from "context/providers";

const getSkillKey = (skill) => `${skill.name}_${skill.category}`;

const SECTION_TITLE_CLASSNAME =
  "font-inter text-base font-semibold text-slate-700";
const SUMMARY_CLASSNAME = "font-inter text-base font-semibold text-slate-700";

const addSkillsToMap = (target, skills = []) => {
  for (const skill of skills) {
    target[getSkillKey(skill)] = skill;
  }
};

const withCandidateExperience = (jobSkill, candidateSkillsObj) => {
  const candidateSkill = candidateSkillsObj[getSkillKey(jobSkill)];

  if (!candidateSkill?.experience) {
    return jobSkill;
  }

  return {
    ...jobSkill,
    experience: candidateSkill.experience,
  };
};

const AllSkillsModal = ({ hit, isOpen, onClose }) => {
  const { jobOpp } = useContext(JobsContext);

  const { jobSkills, matchedJobSkills, missingJobSkills, otherSkills } =
    useMemo(() => {
      const candidateSkillsObj = {};
      const requiredJobSkillsObj = {};
      const optionalJobSkillsObj = {};

      addSkillsToMap(
        candidateSkillsObj,
        (hit.careers || []).flatMap((career) => career?.TKSkills || [])
      );

      addSkillsToMap(
        candidateSkillsObj,
        (hit.resumeWorkHistory || []).flatMap((entry) => entry?.TKSkills || [])
      );

      addSkillsToMap(requiredJobSkillsObj, [
        ...(jobOpp?.intelSkills || []),
        ...(jobOpp?.TKSkills || []),
      ]);

      for (const skill of jobOpp?.optionalTKSkills || []) {
        const skillKey = getSkillKey(skill);
        if (!requiredJobSkillsObj[skillKey]) {
          optionalJobSkillsObj[skillKey] = skill;
        }
      }

      const candidateSkillKeys = new Set(Object.keys(candidateSkillsObj));
      const allJobSkills = [
        ...Object.values(requiredJobSkillsObj),
        ...Object.values(optionalJobSkillsObj),
      ];
      const jobSkillKeys = new Set(allJobSkills.map(getSkillKey));

      const matched = allJobSkills
        .filter((skill) => candidateSkillKeys.has(getSkillKey(skill)))
        .map((skill) => withCandidateExperience(skill, candidateSkillsObj));
      const missing = allJobSkills.filter(
        (skill) => !candidateSkillKeys.has(getSkillKey(skill))
      );
      const other = Object.values(candidateSkillsObj).filter(
        (skill) => !jobSkillKeys.has(getSkillKey(skill))
      );

      return {
        jobSkills: sortSkillsByExperience(allJobSkills),
        matchedJobSkills: sortSkillsByExperience(matched),
        missingJobSkills: sortSkillsByExperience(missing),
        otherSkills: sortSkillsByExperience(other),
      };
    }, [hit.careers, hit.resumeWorkHistory, jobOpp]);

  const renderJobSkillBadge = (skill, hasSkill) => (
    <SkillBadge
      key={`job-${hasSkill ? "matched" : "missing"}-${skill.name}-${
        skill.category
      }`}
      skillName={skill.name}
      category={skill.category}
      experience={hasSkill ? skill.experience : undefined}
      hasSkill={hasSkill}
      noUppercase
      size="sm"
    />
  );

  const renderOtherSkillBadge = (skill) => (
    <SkillBadge
      key={`other-${skill.name}-${skill.category}`}
      skillName={skill.name}
      category={skill.category}
      experience={skill.experience}
      hasSkill
      noUppercase
      size="sm"
    />
  );

  const hasJobSkills =
    matchedJobSkills.length > 0 || missingJobSkills.length > 0;

  return (
    <AppDialog
      open={isOpen}
      onClose={onClose}
      header="All Skills"
      className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg"
      contentClassName="px-6 pb-6 pt-4"
    >
      <div className="flex flex-col gap-y-6 pb-2">
        <p className={SUMMARY_CLASSNAME}>
          {matchedJobSkills.length} out of {jobSkills.length} skills matched
        </p>

        {hasJobSkills ? (
          <div className="flex flex-wrap gap-2">
            {matchedJobSkills.map((skill) => renderJobSkillBadge(skill, true))}
            {missingJobSkills.map((skill) => renderJobSkillBadge(skill, false))}
          </div>
        ) : (
          <p className="font-inter text-sm font-normal text-slate-500">
            No job skills listed for this role.
          </p>
        )}

        {otherSkills.length > 0 ? (
          <section className="space-y-3 border-t border-slate-100 pt-6">
            <h3 className={SECTION_TITLE_CLASSNAME}>Other Skills</h3>
            <div className="flex flex-wrap gap-2">
              {otherSkills.map(renderOtherSkillBadge)}
            </div>
          </section>
        ) : null}
      </div>
    </AppDialog>
  );
};

export default AllSkillsModal;
